import { useLazyQuery } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import TableComponent from '../../components/layout/TableComponent';
import Pagination from '../../components/layout/Pagination';
import NoDataFound from '../../components/layout/NoDataFound';
import Loader from '../../components/layout/Loader';
import InputField from '../../components/layout/InputField';
import { formatDateUS } from '../../utils/commonFn';
import {
  formatStageDisplay,
  getPlacementStatusLabel,
  getStageLabel,
  isPendingPlacementStatus,
  pushInHistory,
} from '../../utils/helper';
import {
  ACTIVITY_DASHBOARD_HEADER_SEARCH,
  ACTIVITY_DASHBOARD_ROUTE,
  COLUMN_INTERFACE_TYPES_FOR_ACTIVITY_DASHBOARD,
  COMPANY_DETAILS_ROUTE,
  PAGE_LIMIT,
  VIEW_CANDIDATE_ROUTE,
  VIEW_JOB_ORDER_ROUTE,
  VIEW_PLACEMENT_MANAGEMENT_ROUTE,
} from '../../utils/constant';
import { GET_ACTIVITY_DASHBOARD } from './gql';
import { ActivityDashboardRow } from '../agreements/interfaces';

let time = 0;

const defaultColumnFilters: COLUMN_INTERFACE_TYPES_FOR_ACTIVITY_DASHBOARD = {
  companySearch: '',
  candidateSearch: '',
  stageSearch: '',
  sorting: { columnName: null, columnValue: null },
};

const ActivityDashboard = ({ location, history }: RouteComponentProps) => {
  const { register, setValue, control } = useForm();

  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [columnFilters, setColumnFilters] =
    useState<COLUMN_INTERFACE_TYPES_FOR_ACTIVITY_DASHBOARD>(defaultColumnFilters);
  const [isSetTimeOut, setIsSetTime] = useState(true);

  const [fetchActivityDashboard, { loading, error, data }] = useLazyQuery(
    GET_ACTIVITY_DASHBOARD,
  );

  const changeFiled = (fieldName: string, value: string | boolean) => {
    if (fieldName?.endsWith('Sort')) {
      setIsSetTime(true);
      const columnName = fieldName.replace('Sort', '');
      setColumnFilters((prev) => ({
        ...prev,
        sorting: { columnName, columnValue: value ? 'ASC' : 'DESC' },
      }));
    } else {
      setCurrentPage(1);
      setIsSetTime(false);
      setColumnFilters((prev) => ({ ...prev, [fieldName]: value }));
    }
  };

  const _fetchActivityDashboard = useCallback(() => {
    const variables = {
      payload: {
        page: currentPage,
        limit,
        companyName: columnFilters.companySearch || undefined,
        candidateName: columnFilters.candidateSearch || undefined,
        stage: columnFilters.stageSearch || undefined,
      },
    };

    if (isSetTimeOut) {
      fetchActivityDashboard({ variables });
    } else {
      window.clearTimeout(time);
      time = window.setTimeout(() => {
        fetchActivityDashboard({ variables });
      }, 1000);
    }
  }, [currentPage, limit, columnFilters, isSetTimeOut, fetchActivityDashboard]);

  useEffect(() => {
    _fetchActivityDashboard();
  }, [_fetchActivityDashboard]);

  const items: ActivityDashboardRow[] = data?.getActivityDashboard?.items ?? [];
  const totalCount = data?.getActivityDashboard?.count ?? 0;
  const pageCount = Math.ceil(totalCount / limit) || 1;
  const start = (currentPage - 1) * limit;

  return (
    <div className="admin-tabs team-tabs">
      <div className="primaryHeading">
        <h5>Activity Dashboard</h5>
      </div>

      <div className="table-responsive mt-3">
        <TableComponent
          advanceSearching
          tableHeaderAdvSeacrh={ACTIVITY_DASHBOARD_HEADER_SEARCH}
          handleChange={(fieldName, value) => changeFiled(fieldName, value)}
          columnFilters={columnFilters}
          register={register}
          setValue={setValue}
          control={control}
          tableName="ActivityDashboard"
        >
          {loading && (
            <tbody>
              <tr>
                <td colSpan={5}>
                  <Loader loaderClass="sm-height " />
                </td>
              </tr>
            </tbody>
          )}

          {!loading && items.length === 0 && (
            <tbody>
              <tr>
                <td colSpan={5} className="empty-table-td">
                  <NoDataFound text="No activity found" />
                </td>
              </tr>
            </tbody>
          )}

          {!loading && items.length > 0 && (
            <tbody>
              {items.map((row) => (
                <tr key={row.id}>
                  <td
                    className="route-link text-truncate max-200"
                    onClick={() => {
                      history.push({
                        pathname: `${COMPANY_DETAILS_ROUTE}/${row.companyId}`,
                        state: { backUrl: ACTIVITY_DASHBOARD_ROUTE },
                      });
                      pushInHistory(ACTIVITY_DASHBOARD_ROUTE, location.search);
                    }}
                  >
                    <div>{row.companyName ?? '--'}</div>
                    <div className="text-muted small">
                      {row.jobOrder ?? '--'}
                    </div>
                  </td>
                  <td
                    className="route-link text-truncate max-200"
                    onClick={() => {
                      history.push({
                        pathname: `${VIEW_CANDIDATE_ROUTE}/${row.candidateId}`,
                        state: { backUrl: ACTIVITY_DASHBOARD_ROUTE },
                      });
                      pushInHistory(ACTIVITY_DASHBOARD_ROUTE, location.search);
                    }}
                  >
                    {row.candidateName ?? '--'}
                  </td>
                  <td>
                    <span className="text-capitalize badge badge-info">
                      {getStageLabel(row.latestStage)}
                    </span>
                    {row.latestStage?.toUpperCase() === 'SUBMITTED' &&
                      row.submissionStatus && (
                        <span
                          className={`text-capitalize badge ml-1 badge-${row.submissionStatus === 'COMPLETED'
                            ? 'success'
                            : 'primary'
                            }`}
                        >
                          {row.submissionStatus === 'COMPLETED'
                            ? 'COMPLETED'
                            : 'PENDING'}
                        </span>
                      )}
                    {row.latestStage?.toUpperCase() === 'PLACED' &&
                      isPendingPlacementStatus(row.placementStatus) && (
                        <span className="text-capitalize badge ml-1 badge-warning">
                          {getPlacementStatusLabel(row.placementStatus!)}
                        </span>
                      )}
                  </td>
                  <td
                    className="route-link"
                    onClick={() => {
                      const stageUpper = row.latestStage?.toUpperCase();
                      if (stageUpper === 'PLACED' && row.placementId) {
                        history.push({
                          pathname: `${VIEW_PLACEMENT_MANAGEMENT_ROUTE}/${row.placementId}`,
                          state: { backUrl: ACTIVITY_DASHBOARD_ROUTE },
                        });
                      } else if (row.jobOrderId) {
                        const statusValue =
                          stageUpper === 'SUBMITTED' ? 'Submitted' :
                          stageUpper === 'INTERVIEWING' ? 'Interviewed' :
                          stageUpper === 'OFFERED' ? 'Offered' : '';
                        const candidateName = encodeURIComponent(row.candidateName ?? '');
                        history.push({
                          pathname: `${VIEW_JOB_ORDER_ROUTE}/${row.jobOrderId}`,
                          search: `?activeTab=6${statusValue ? `&statusFilter=${statusValue}` : ''}${candidateName ? `&candidateName=${candidateName}` : ''}`,
                          state: { backUrl: ACTIVITY_DASHBOARD_ROUTE },
                        });
                      }
                      pushInHistory(ACTIVITY_DASHBOARD_ROUTE, location.search);
                    }}
                  >
                    {formatStageDisplay(row)}
                  </td>
                  <td>
                    {row.jobApplicantUpdatedAt
                      ? formatDateUS(row.jobApplicantUpdatedAt)
                      : '--'}
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </TableComponent>
      </div>

      {!loading && totalCount > 0 && (
        <div className="users-pagination">
          <Pagination
            onPageChange={(pageClicked: number) => {
              setIsSetTime(true);
              setCurrentPage(pageClicked);
            }}
            pageCount={pageCount}
            currentPage={currentPage}
          />

          <div className="d-flex align-items-center">
            <div className="pagination-display paginationItemsSelect">
              <p className="m-0">
                {start + 1} to {Math.min(start + limit, totalCount)} of{' '}
                {totalCount}
              </p>

              <div className="divider"></div>

              <label>Display</label>

              <InputField
                label=""
                inputtype="select"
                selectItems={PAGE_LIMIT.filter((item) => item.value !== limit)}
                inputid="limit"
                placeholder={limit.toString()}
                inputMethod={(value: string) => {
                  setIsSetTime(true);
                  setCurrentPage(1);
                  setLimit(parseInt(value, 10));
                }}
                inputValue={limit}
              />
            </div>
          </div>
        </div>
      )}

      {error && (
        <small className="text-danger d-block mt-2">
          {error.message || 'Failed to load activity dashboard.'}
        </small>
      )}
    </div>
  );
};

export default ActivityDashboard;
